import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { CanvasTexture } from 'three';

// Single symbol particle group
function SymbolParticles({ symbol, count = 15 }) {
  const points = useRef();
  
  const particlesPosition = useMemo(() => {
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 30;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 25 + 5;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 15;
    }
    return positions;
  }, [count]);

  // Create texture for this specific symbol
  const symbolTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    
    ctx.fillStyle = 'rgba(0, 122, 204, 0.7)';
    ctx.font = 'bold 48px Consolas, monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(symbol, 32, 32);
    
    return new CanvasTexture(canvas);
  }, [symbol]);

  useFrame((state) => {
    if (points.current) {
      points.current.rotation.y = state.clock.getElapsedTime() * 0.05;
      points.current.rotation.x = Math.sin(state.clock.getElapsedTime() * 0.03) * 0.2;
    }
  });

  return (
    <points ref={points}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particlesPosition.length / 3}
          array={particlesPosition}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial 
        size={0.3} 
        color="#007acc" 
        transparent 
        opacity={0.6} 
        sizeAttenuation={true}
        map={symbolTexture}
        alphaTest={0.1}
        depthWrite={false}
      />
    </points>
  );
}

// All code particles
function CodeParticles() {
  const symbols = ['{', '}', '(', ')', '[', ']', '<', '>', ';', ':', '/', '*', '=', '+', '-', '0', '1'];
  
  return (
    <group>
      {symbols.map((symbol, i) => (
        <SymbolParticles key={i} symbol={symbol} count={12} />
      ))}
    </group>
  );
}

// Main Scene
function Scene() {
  return (
    <>
      <ambientLight intensity={0.5} />
      <CodeParticles />
    </>
  );
}

// Main Component
export default function FloatingBackground() {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 0,
      pointerEvents: 'none',
      opacity: 1
    }}>
      <Canvas
        camera={{ position: [0, 0, 5], fov: 75 }}
        style={{ background: 'transparent' }}
      >
        <Scene />
      </Canvas>
    </div>
  );
}
